import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useLocation } from "wouter";
import QRCode from "qrcode";
import { Button } from "@/components/ui/button";
import { Loader2, Printer, ArrowLeft, CheckCircle2, Clock, XCircle } from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { apiRequest } from "@/lib/queryClient";
import { montarPixCopiaCola } from "@/lib/pix";

type Empresa = {
  id: string;
  razaoSocial: string;
  nomeFantasia: string | null;
  cnpj: string | null;
  cpf: string | null;
  email: string;
  telefone: string | null;
  whatsapp: string | null;
  logradouro: string | null;
  numero: string | null;
  bairro: string | null;
  cidade: string;
  uf: string;
  cep: string | null;
};

type FaturaParceiroItem = {
  id: string;
  laudoIdTexto: string | null;
  placa: string | null;
  tipoVeiculo: string;
  valorUnitario: string;
  descricao: string | null;
};

type FaturaParceiro = {
  id: string;
  numero: number;
  mes: number;
  ano: number;
  status: string;
  valorTotal: string;
  vencimento: string;
  observacoes: string | null;
  chavePix: string | null;
  pagoEm: string | null;
  criadoEm: string;
  vistoriadora?: Empresa;
  parceiro?: Empresa;
  itens?: FaturaParceiroItem[];
};

const MESES = [
  "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro",
];

const TIPO_VEICULO_LABELS: Record<string, string> = {
  auto_leve: "Auto Leve",
  moto: "Moto",
  caminhao: "Caminhão",
  onibus: "Ônibus",
};

function formatDate(dateStr: string) {
  return new Date(dateStr + "T00:00:00").toLocaleDateString("pt-BR");
}

function formatCNPJ(cnpj: string) {
  return cnpj.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
}

function formatCEP(cep: string) {
  return cep.replace(/(\d{5})(\d{3})/, "$1-$2");
}

function enderecoEmpresa(e: Empresa) {
  const parts = [
    e.logradouro && e.numero ? `${e.logradouro}, ${e.numero}` : e.logradouro,
    e.bairro,
    `${e.cidade} - ${e.uf}`,
    e.cep ? formatCEP(e.cep) : null,
  ].filter(Boolean);
  return parts.join(" · ");
}

const STATUS_LABEL: Record<string, { label: string; color: string }> = {
  pendente: { label: "PENDENTE", color: "#f59e0b" },
  pago: { label: "PAGO", color: "#16a34a" },
  cancelado: { label: "CANCELADO", color: "#6b7280" },
};

export default function RelatorioParceiro() {
  const { id } = useParams<{ id: string }>();
  const [, setLocation] = useLocation();

  const { data: fatura, isLoading, isError } = useQuery<FaturaParceiro>({
    queryKey: ["fatura-parceiro", id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/faturas-parceiro/${id}`);
      return res.json();
    },
    enabled: !!id,
  });

  const [pixQrDataUrl, setPixQrDataUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!fatura?.chavePix || !fatura.vistoriadora) {
      setPixQrDataUrl(null);
      return;
    }
    const payload = montarPixCopiaCola({
      chave: fatura.chavePix,
      nomeRecebedor: fatura.vistoriadora.nomeFantasia || fatura.vistoriadora.razaoSocial,
      cidade: fatura.vistoriadora.cidade,
      valor: Number(fatura.valorTotal),
    });
    QRCode.toDataURL(payload, { margin: 1, width: 160 }).then(setPixQrDataUrl).catch(() => setPixQrDataUrl(null));
  }, [fatura]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (isError || !fatura) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-4">
        <p className="text-muted-foreground">Fatura não encontrada.</p>
        <Button onClick={() => setLocation("/faturas-parceiro")}>Voltar</Button>
      </div>
    );
  }

  const statusInfo = STATUS_LABEL[fatura.status] ?? { label: fatura.status.toUpperCase(), color: "#6b7280" };
  const vistoriadora = fatura.vistoriadora;
  const parceiro = fatura.parceiro;
  const itens = fatura.itens ?? [];
  const numeroFormatado = `FP-${fatura.ano}-${String(fatura.numero).padStart(4, "0")}`;

  return (
    <>
      {/* Barra de ações (não aparece no print) */}
      <div className="print:hidden flex items-center justify-between p-4 bg-white border-b shadow-sm sticky top-0 z-10">
        <Button
          variant="ghost"
          className="gap-2"
          onClick={() => setLocation("/faturas-parceiro")}
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </Button>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground">
            Relatório de Faturamento · {numeroFormatado}
          </span>
          <Button className="gap-2" onClick={() => window.print()}>
            <Printer className="w-4 h-4" />
            Imprimir / Salvar PDF
          </Button>
        </div>
      </div>

      {/* Documento */}
      <div
        id="relatorio-print"
        className="max-w-4xl mx-auto bg-white p-10 print:p-8 print:max-w-none print:shadow-none shadow-md my-6 print:my-0"
        style={{ fontFamily: "'Inter', sans-serif" }}
      >
        {/* Cabeçalho */}
        <div className="flex items-start justify-between mb-8 pb-6 border-b-2 border-slate-200">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">
              RELATÓRIO DE FATURAMENTO
            </h1>
            <p className="text-slate-500 mt-1">Documento de Cobrança</p>
          </div>
          <div className="text-right">
            <p
              className="text-2xl font-bold tracking-widest px-4 py-1 rounded"
              style={{ color: statusInfo.color, border: `2px solid ${statusInfo.color}` }}
            >
              {statusInfo.label}
            </p>
            <p className="text-sm text-slate-500 mt-2">Nº {numeroFormatado}</p>
          </div>
        </div>

        {/* Dados das partes */}
        <div className="grid grid-cols-2 gap-8 mb-8">
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Emitido por</p>
            {vistoriadora ? (
              <div className="space-y-1">
                <p className="font-bold text-slate-800 text-lg">
                  {vistoriadora.nomeFantasia || vistoriadora.razaoSocial}
                </p>
                {vistoriadora.nomeFantasia && (
                  <p className="text-sm text-slate-600">{vistoriadora.razaoSocial}</p>
                )}
                {vistoriadora.cnpj && (
                  <p className="text-sm text-slate-600">CNPJ: {formatCNPJ(vistoriadora.cnpj)}</p>
                )}
                {vistoriadora.email && (
                  <p className="text-sm text-slate-600">{vistoriadora.email}</p>
                )}
                {(vistoriadora.whatsapp || vistoriadora.telefone) && (
                  <p className="text-sm text-slate-600">
                    {vistoriadora.whatsapp || vistoriadora.telefone}
                  </p>
                )}
                <p className="text-sm text-slate-500">{enderecoEmpresa(vistoriadora)}</p>
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Dados não disponíveis</p>
            )}
          </div>

          <div>
            <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">Cobrado de</p>
            {parceiro ? (
              <div className="space-y-1">
                <p className="font-bold text-slate-800 text-lg">
                  {parceiro.nomeFantasia || parceiro.razaoSocial}
                </p>
                {parceiro.nomeFantasia && (
                  <p className="text-sm text-slate-600">{parceiro.razaoSocial}</p>
                )}
                {parceiro.cnpj && (
                  <p className="text-sm text-slate-600">CNPJ: {formatCNPJ(parceiro.cnpj)}</p>
                )}
                {parceiro.cpf && !parceiro.cnpj && (
                  <p className="text-sm text-slate-600">CPF: {parceiro.cpf}</p>
                )}
                {parceiro.email && (
                  <p className="text-sm text-slate-600">{parceiro.email}</p>
                )}
                {(parceiro.whatsapp || parceiro.telefone) && (
                  <p className="text-sm text-slate-600">
                    {parceiro.whatsapp || parceiro.telefone}
                  </p>
                )}
                <p className="text-sm text-slate-500">{enderecoEmpresa(parceiro)}</p>
              </div>
            ) : (
              <p className="text-slate-400 text-sm">Dados não disponíveis</p>
            )}
          </div>
        </div>

        {/* Dados da fatura */}
        <div className="grid grid-cols-3 gap-4 mb-8 bg-slate-50 rounded-xl p-4">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Período de referência</p>
            <p className="font-semibold text-slate-800 mt-1">
              {MESES[fatura.mes - 1]} / {fatura.ano}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Data de emissão</p>
            <p className="font-semibold text-slate-800 mt-1">
              {new Date(fatura.criadoEm).toLocaleDateString("pt-BR")}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Vencimento</p>
            <p className="font-semibold text-slate-800 mt-1">{formatDate(fatura.vencimento)}</p>
          </div>
          {fatura.pagoEm && (
            <div className="col-span-3">
              <p className="text-xs text-slate-500 uppercase tracking-wider">Data do pagamento</p>
              <p className="font-semibold text-green-700 mt-1">
                {new Date(fatura.pagoEm).toLocaleDateString("pt-BR")}
              </p>
            </div>
          )}
        </div>

        {/* Tabela de itens */}
        <div className="mb-8">
          <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
            Detalhamento dos Serviços
          </p>
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-slate-800 text-white text-sm">
                <th className="text-left py-3 px-4 rounded-tl-lg font-semibold">Nº Laudo</th>
                <th className="text-left py-3 px-4 font-semibold">Placa</th>
                <th className="text-left py-3 px-4 font-semibold">Tipo de Veículo</th>
                <th className="text-left py-3 px-4 font-semibold">Descrição</th>
                <th className="text-right py-3 px-4 rounded-tr-lg font-semibold">Valor</th>
              </tr>
            </thead>
            <tbody>
              {itens.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-6 text-slate-400 text-sm">
                    Nenhum item registrado
                  </td>
                </tr>
              ) : (
                itens.map((item, idx) => (
                  <tr
                    key={item.id}
                    className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}
                  >
                    <td className="py-3 px-4 text-sm text-slate-700 font-mono">
                      {item.laudoIdTexto || "—"}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700 font-mono font-bold">
                      {item.placa || "—"}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-700">
                      {TIPO_VEICULO_LABELS[item.tipoVeiculo] || item.tipoVeiculo}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-600">
                      {item.descricao || "Laudo Técnico de Desmonte"}
                    </td>
                    <td className="py-3 px-4 text-sm text-slate-800 font-semibold text-right">
                      {formatCurrency(Number(item.valorUnitario))}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Total */}
        <div className="flex justify-end mb-8">
          <div className="w-64 border-2 border-slate-800 rounded-xl overflow-hidden">
            <div className="bg-slate-800 text-white px-5 py-3 flex items-center justify-between">
              <span className="font-bold text-sm uppercase tracking-wider">Total</span>
              <span className="font-bold text-xl">
                {formatCurrency(Number(fatura.valorTotal))}
              </span>
            </div>
            <div className="px-5 py-2 text-xs text-slate-500 flex justify-between">
              <span>{itens.length} item(s)</span>
              <span>BRL</span>
            </div>
          </div>
        </div>

        {/* Pagamento via PIX */}
        {fatura.chavePix && (
          <div className="mb-8 bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-center justify-between gap-6">
            <div>
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">
                Pagamento via PIX
              </p>
              <p className="text-sm text-slate-700">Chave PIX:</p>
              <p className="text-sm font-mono font-semibold text-slate-800 break-all">
                {fatura.chavePix}
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Escaneie o QR Code ao lado com o app do seu banco — valor já preenchido.
              </p>
            </div>
            {pixQrDataUrl && (
              <img
                src={pixQrDataUrl}
                alt="QR Code PIX"
                className="w-32 h-32 shrink-0 rounded-lg border border-emerald-200 bg-white p-1"
              />
            )}
          </div>
        )}

        {/* Observações */}
        {fatura.observacoes && (
          <div className="mb-8 bg-amber-50 border border-amber-200 rounded-xl p-4">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider mb-2">
              Observações / Dados para Pagamento
            </p>
            <p className="text-sm text-slate-700 whitespace-pre-wrap">{fatura.observacoes}</p>
          </div>
        )}

        {/* Rodapé */}
        <div className="border-t-2 border-slate-200 pt-6 mt-6">
          <div className="flex items-start justify-between text-xs text-slate-400">
            <div>
              <p className="font-semibold text-slate-600 mb-1">LaudoLegal — Gestão Técnica Veicular</p>
              <p>Documento gerado em {new Date().toLocaleDateString("pt-BR")} às {new Date().toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}</p>
              <p className="mt-1">Este documento é um relatório de faturamento e não substitui Nota Fiscal Eletrônica (NF-e).</p>
            </div>
            <div className="text-right">
              <p>Referente à Lei Federal nº 12.977/14</p>
              <p>Laudo Técnico de Desmonte</p>
            </div>
          </div>
        </div>
      </div>

      {/* CSS de impressão */}
      <style>{`
        @media print {
          body { background: white !important; }
          #relatorio-print {
            box-shadow: none !important;
            margin: 0 !important;
            padding: 24px !important;
          }
          @page {
            margin: 1.5cm;
            size: A4;
          }
        }
      `}</style>
    </>
  );
}
